import { CheckIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "cn";

interface ChipRadioGroupProps {
  onChange: (newFilter: string | undefined) => unknown;
  options: {
    label: string | React.ReactElement;
    value: unknown;
  }[];
  value?: unknown;
  disabled?: boolean;
}

function ChipRadioGroup(props: ChipRadioGroupProps) {
  const { onChange, options, value, disabled } = props;
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Commit filter value synchronously so list / gated UI doesn't lag the chip.
  const handleChange = (next: string | null) => {
    const nextValue = next ?? undefined;
    setLocalValue(nextValue);
    onChange(nextValue);
  };

  return (
    <div
      role="radiogroup"
      aria-disabled={disabled}
      className="flex flex-row flex-wrap gap-2 py-2"
    >
      {options?.map((option, i) => {
        const optionValue = String(option.value);
        const checked = localValue != null && String(localValue) === optionValue;

        return (
          <button
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={disabled}
            key={`${optionValue}-${i}`}
            onClick={() => handleChange(optionValue)}
            className={cn(
              // Shape/sizing unchanged; checked mirrors Checkbox soft inverse
              // (primary border + check, primary-foreground fill). Unselected
              // keeps the same primary edge; fill/text carry selection.
              "relative flex h-8 w-fit cursor-pointer items-center justify-center rounded-full border border-primary bg-background px-3 text-sm leading-none font-medium shadow-xs",
              "hover:bg-primary-foreground",
              checked &&
              "bg-primary-foreground text-primary hover:bg-primary-foreground hover:text-primary",
              disabled && "cursor-not-allowed opacity-50"
            )}
          >
            {checked ? <CheckIcon className="mr-2 size-4 shrink-0" /> : null}
            <span className="min-w-0">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default ChipRadioGroup;
